<template>
  <div class="trigger-configuration-wrapper">
    <div class="add-user-header-div">
      <div class="add-user-title">
        <div class="action-provider-settings-title">
          <h4>When should it be initiated</h4>
          <div style="display: block" v-html="helpText"></div>
        </div>
      </div>
      <div class="flex justify-end gap-3">
        <RfButtonNew type="tertiary" :disabled="isDisabled" @click="showCreateTriggerDialog">
          <RfEditIcon class="h-5 w-5" />
          <span>Create new trigger</span>
        </RfButtonNew>
        <RfButtonNew type="secondary" :disabled="isDisabled" @click="showAddNewTriggerDialog">
          <RfAddIcon class="h-5 w-5" />
          <span>Select & Add trigger</span>
        </RfButtonNew>
      </div>
    </div>
    <RfDialog
      ref="selectTriggerDialog"
      width="800"
      height="600"
      title="Select a trigger"
      confirmText="Add"
      keepOpenOnConfirm
      @confirm="submitTrigger"
      @close="onSelectTriggerClose"
    >
      <div class="flex h-full flex-col">
        <RfAlert v-if="hasInjectTrigger" type="error" class="mb-4">
          Warning: Adding a trigger will delete any trigger added via Real Time
        </RfAlert>
        <div v-if="appTriggerItems.length" class="flex min-h-0 flex-1 flex-col gap-2">
          <RfInput
            v-model="triggerSearch"
            variant="outlined"
            autoWidth
            clearable
            class="w-full shrink-0"
            placeholder="Search triggers"
          />
          <div class="flex flex-col gap-2 overflow-y-auto">
            <RfCheckbox
              v-for="trigger in filteredAppTriggerItems"
              :key="trigger.value.id"
              :modelValue="addedTriggers.includes(trigger.value)"
              @update:modelValue="checked => toggleAddedTrigger(trigger.value, checked)"
              >{{ trigger.text }}</RfCheckbox
            >
          </div>
          <div v-if="!filteredAppTriggerItems.length" class="text-body text-grey-1 text-center">
            No triggers match your search.
          </div>
        </div>
        <div v-else class="flex items-center justify-center gap-2">
          There are no existing triggers.
          <RfButtonNew
            :to="`/apps/${appsStore.app.id}/settings/triggers`"
            type="link"
            size="link"
            class="cursor-pointer"
            text="Click here to add some."
          />
        </div>
      </div>
    </RfDialog>
    <RfEditTrigger
      v-if="createTriggerDialog"
      :key="editingTrigger?.id"
      :editTrigger="editingTrigger"
      :creating="creating"
      :app="appsStore.app"
      :parentTriggerType="
        model.device_type === 'web'
          ? 'web'
          : model.path_type === 'scheduled_push'
            ? 'push_event'
            : 'device'
      "
      @closeNewTriggerDialog="closeCreateTriggerDialog"
    />
    <RfBaseModal
      ref="advancedArgsModal"
      title="Advanced Arguments"
      width="550"
      primaryText="Done"
      @close="() => (editArgsTriggerIndex = null)"
      @primary="submitArgs"
    >
      <template #body>
        <RfAdvancedArguments ref="advancedArgsBody" />
      </template>
    </RfBaseModal>
    <RfTable
      :tableData="{ totalCount: triggers.length }"
      type="triggers"
      hideTotal
      noIntersection
      noDataText=" You don't have any triggers yet. Add a new trigger."
      class="w-full [&_.rf-table--wrapper]:rounded-none [&_table]:rounded-none"
    >
      <template #thead>
        <tr>
          <th class="tbl-col-1">Name</th>
          <th class="tbl-col-1">{{ urlPathHeader }}</th>
          <th class="tbl-col-2">Regex</th>
          <th class="tbl-col-5">Delay</th>
          <th class="tbl-col-3">{{ clickIdHeader }}</th>
          <th v-if="triggerType === 'web'" class="tbl-col-4">Click count</th>
          <th
            v-if="
              triggerType === 'web' ||
              (triggerType === 'device' && appsStore.app?.flags?.custom_devices_js_sdk)
            "
            class="tbl-col-4"
          >
            Advanced
          </th>
          <th v-if="triggerType === 'web'" class="tbl-col-4">Interval (seconds)</th>
          <th v-if="hasAdvancedArgs" class="tbl-col-4">Arguments</th>
          <th v-if="!isStandalone" class="tbl-col-4">Added via Real Time</th>
          <template v-if="appsStore.app?.flags?.scheduled_push">
            <th class="tbl-col-tr">Event name</th>
            <th class="tbl-col-tr">Event attributes</th>
          </template>
          <th class="tbl-col-4"></th>
        </tr>
      </template>
      <template #tbody>
        <tr v-for="(trigger, index) in triggerDisplay" :key="index">
          <template v-if="trigger">
            <td>{{ trigger.name || "Unnamed Trigger" }}</td>
            <td>{{ displayPage(trigger) }}</td>
            <td>
              <div v-if="regexDisplay(trigger)">{{ regexDisplay(trigger) }}</div>
              <div v-else class="setting-empty-val">N/A</div>
            </td>
            <td>{{ trigger.delay_seconds || "0" }}</td>
            <td>{{ trigger.click_id || "None" }}</td>
            <td v-if="triggerType === 'web'">{{ trigger.click_count_threshold || null }}</td>
            <td
              v-if="
                triggerType === 'web' ||
                (triggerType === 'device' && appsStore.app?.flags?.custom_devices_js_sdk)
              "
            >
              {{ displayCustomTrigger(trigger, customTriggers) || "None" }}
            </td>
            <td v-if="triggerType === 'web'">
              <div v-if="displayCustomInterval(trigger, undefined, eventInterval)">
                {{ displayCustomInterval(trigger, undefined, eventInterval) }}
              </div>
              <div v-else class="setting-empty-val">N/A</div>
            </td>
            <td v-if="hasAdvancedArgs">
              <div v-if="trigger.custom_trigger_id">
                {{ objectToString(trigger.custom_trigger_args) }}
                <button @click="editTriggerArgs(index, trigger.custom_trigger_args)">
                  <RfSettingsIcon class="fill-beige-50 inline h-6 w-6" />
                </button>
              </div>
              <div v-else class="setting-empty-val">N/A</div>
            </td>
            <td v-if="!isStandalone">
              <RfCheckbox
                :modelValue="trigger.trigger_type === 'easy_button'"
                disabled
                size="small"
              />
            </td>
            <template v-if="appsStore.app?.flags?.scheduled_push">
              <td class="tbl-col-tr">{{ trigger.event_name }}</td>
              <td class="tbl-col-tr">
                {{ objectToString(trigger.event_attributes) }}
              </td>
            </template>
            <td align="right">
              <div class="flex items-center justify-end gap-1">
                <RfButtonNew
                  type="table"
                  :disabled="isEditBtnDisabled"
                  @click="editTrigger(trigger)"
                >
                  <RfEditIcon class="fill-beige-50 h-6 w-6" />
                </RfButtonNew>
                <RfButtonNew type="table" @click="deleteTrigger(trigger)">
                  <RfDeleteIcon class="fill-vermillion-50 h-6 w-6" />
                </RfButtonNew>
              </div>
            </td>
          </template>
        </tr>
      </template>
    </RfTable>
  </div>
</template>

<script>
import RfEditTrigger from "@/components/RfCommonCards/RfEditTrigger.vue";
import { cloneDeep } from "lodash-es";
import RfAdvancedArguments from "@/blocks/RfAdvancedArguments.vue";
import { isStandalone } from "@/utils/prompts/promptHelpers";
import {
  displayCustomInterval,
  displayCustomTrigger,
  displayPage,
  regexDisplay,
} from "@/utils/triggerHelpers";
import { objectToString } from "@/utils/objectUtils";
import { useTriggersStore } from "@/pinia/triggersStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useActionsStore } from "@/pinia/actionsStore";
import RfBaseModal from "../modals/RfBaseModal.vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfAddIcon from "@/components/icons/RfAddIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfSettingsIcon from "@/components/icons/RfSettingsIcon.vue";
import RfTable from "@/blocks/RfTable/RfTable.vue";

export default {
  name: "RfTrigger",
  components: {
    RfEditTrigger,
    RfBaseModal,
    RfAdvancedArguments,
    RfAlert,
    RfDialog,
    RfButtonNew,
    RfCheckbox,
    RfInput,
    RfTable,
    RfEditIcon,
    RfAddIcon,
    RfDeleteIcon,
    RfSettingsIcon,
  },
  props: {
    model: { type: Object, required: true },
    isDisabled: { type: Boolean, default: false },
    isEditBtnDisabled: { type: Boolean, default: false },
  },
  setup: () => ({
    displayCustomInterval,
    regexDisplay,
    displayPage,
    displayCustomTrigger,
    objectToString,
    appsStore: useAppsStore(),
    actionsStore: useActionsStore(),
    triggersStore: useTriggersStore(),
  }),
  data() {
    return {
      triggers: cloneDeep(this.model.triggers).sort((a, b) =>
        `${a.name}`.localeCompare(`${b.name}`),
      ),
      eventInterval: 7200,
      triggerSearch: "",
      addedTriggers: [],
      toDelete: null,
      createTriggerDialog: false,
      creating: false,
      editingTrigger: null,
      editArgsTriggerIndex: null,
    };
  },
  computed: {
    clientActions() {
      return this.actionsStore.clientActions || [];
    },
    triggerType() {
      if (this.model.device_type === "web") return "web";
      if (this.model.path_type === "scheduled_push") return "push_event";
      return "device";
    },
    customTriggers() {
      const blank = [{ text: "None", value: "" }];
      const actions = this.clientActions.filter(action => action.action_type === "custom_trigger");
      return blank.concat(actions);
    },
    helpText() {
      let text =
        "Specify one or more trigger events. Multiple triggers are combined using OR logic.";
      if (this.isDisabled) {
        text += " Triggers cannot be changed when an experiment is running.";
      }
      return text;
    },
    triggerDisplay() {
      return this.triggers.map(el => {
        const storeTrigger = this.triggersStore.triggers.find(item => item.id === el.id) || el;
        const copiedTrigger = { ...storeTrigger };
        copiedTrigger.custom_trigger_args = el.custom_trigger_args;
        return copiedTrigger;
      });
    },
    isStandalone() {
      return isStandalone(this.model);
    },
    urlPathHeader() {
      return this.triggerType === "web" ? "Page" : "Screen";
    },
    clickIdHeader() {
      return this.triggerType === "web" ? "Query Selector" : "Button Name";
    },
    triggerIds() {
      return this.triggers.map(trigger => trigger.id);
    },
    appTriggerItems() {
      return this.triggersStore.triggers
        .filter(
          trigger =>
            trigger.trigger_type === this.triggerType && this.triggerIds.indexOf(trigger.id) < 0,
        )
        .map(trigger => {
          return {
            text: trigger.name || "Unnamed Trigger",
            value: trigger,
          };
        });
    },
    sortedAppTriggerItems() {
      return [...this.appTriggerItems].sort((a, b) => `${a.text}`.localeCompare(`${b.text}`));
    },
    filteredAppTriggerItems() {
      const search = this.triggerSearch.trim().toLowerCase();
      if (!search) return this.sortedAppTriggerItems;
      return this.sortedAppTriggerItems.filter(trigger =>
        trigger.text.toLowerCase().includes(search),
      );
    },
    hasInjectTrigger() {
      return !!this.triggers.find(trigger => trigger.trigger_type === "easy_button");
    },
    hasAdvancedArgs() {
      return (
        this.triggerType === "web" ||
        (this.triggerType === "device" && this.appsStore.app?.flags?.custom_devices_js_sdk)
      );
    },
  },
  mounted() {
    this.triggersStore.getTriggers(this.appsStore.app.id);
    if (!this.clientActions.length) this.actionsStore.getClientActions(this.appsStore.app.id);
  },
  methods: {
    fillModel(model) {
      model.triggers = cloneDeep(this.triggerDisplay);
      if (this.toDelete) {
        model.toDelete = this.toDelete;
        this.toDelete = null;
      }
    },
    showAddNewTriggerDialog() {
      this.addedTriggers = [];
      this.triggerSearch = "";
      this.$refs.selectTriggerDialog?.open();
    },
    toggleAddedTrigger(value, checked) {
      if (checked) {
        if (!this.addedTriggers.includes(value)) this.addedTriggers.push(value);
      } else {
        this.addedTriggers = this.addedTriggers.filter(item => item !== value);
      }
    },
    onSelectTriggerClose() {
      this.addedTriggers = [];
      this.triggerSearch = "";
    },
    showCreateTriggerDialog() {
      this.editingTrigger = null;
      this.creating = true;
      this.createTriggerDialog = true;
    },
    closeCreateTriggerDialog(triggerToAssign) {
      this.createTriggerDialog = false;
      if (this.creating && triggerToAssign?.id) {
        const foundTrigger = this.triggersStore.triggers.find(el => el.id === triggerToAssign.id);
        if (foundTrigger) {
          this.addedTriggers.push(foundTrigger);
          this.submitTrigger();
        }
      }
    },
    submitTrigger() {
      if (this.hasInjectTrigger) {
        const inject = this.triggers.find(item => item.trigger_type === "easy_button");
        this.toDelete = inject.id;
        const index = this.triggers.indexOf(inject);
        this.triggers.splice(index, 1);
      }
      this.triggers = this.triggers.concat(this.addedTriggers);
      this.addedTriggers = [];
      this.$refs.selectTriggerDialog?.close();
    },
    editTrigger(trigger) {
      this.editingTrigger = cloneDeep(trigger);
      this.creating = false;
      this.createTriggerDialog = true;
    },
    deleteTrigger(trigger) {
      const index = this.triggers.findIndex(item => item.id === trigger.id);
      if (index !== -1) this.triggers.splice(index, 1);
    },
    editTriggerArgs(index, args) {
      this.editArgsTriggerIndex = index;
      const trigger = this.triggers[index];
      const fieldData = (this.clientActions || []).find(
        ({ id }) => id === trigger.custom_trigger_id,
      )?.arg_metadata;
      this.$refs.advancedArgsModal.show();
      this.$nextTick(() =>
        this.$refs.advancedArgsBody.init(
          fieldData && !Object.keys(args || []).length ? [""] : args,
          fieldData,
        ),
      );
    },
    submitArgs() {
      this.triggers.splice(this.editArgsTriggerIndex, 1, {
        ...this.triggers[this.editArgsTriggerIndex],
        custom_trigger_args: this.$refs.advancedArgsBody.get(),
      });
      this.$refs.advancedArgsModal.close();
    },
    updateTriggers(trigger) {
      if (!trigger?.id) return;

      if (this.creating && this.triggers.map(item => item.id).indexOf(trigger.id) < 0) {
        this.triggers = [...this.triggers, trigger];
      } else {
        this.triggers = this.triggers.map(item => (item.id === trigger.id ? trigger : item));
      }
      this.creating = false;
    },
  },
};
</script>
<style scoped>
.config-trigger-specific-pg {
  margin: 6px 0 0 2rem;
}
</style>
